import { FunctionComponent, memo, MouseEvent, ReactElement, ReactNode, useCallback, useMemo, useState } from 'react';
import { useStoreMap } from 'effector-react';
import classNames from 'classnames';
import { Avatar } from '@nx-frontend/shared/ui/avatar';
import { Button, ButtonSize, ButtonType } from '@nx-frontend/shared/ui/button';
import { getIconByMenuItem, getMenuItemIcon, Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { TippyTooltip, Tooltip } from '@nx-frontend/shared/ui/tooltip';
import { getItemOffset } from '@nx-frontend/shared/store/feature-menu-tree';
import {
  isSectionMenuItemType,
  MenuItem,
  MenuItemCustomClass,
  MenuItemType,
  MenuTooltipMap,
  MenuTooltipPosition,
  MenuType,
  OrgMemberRole,
  Permissions,
  UserWithRoleAndAccess,
} from '@nx-frontend/shared/types';
import { UiTooltipPosition } from '@nx-frontend/shared/types';
import { IconKey } from '@nx-frontend/shared/types/icon';
import { getUserDisplayName } from '@nx-frontend/shared/utils';
import { MenuTreeItemAction } from './menu-tree-item-action';
import { MenuTreeItemDraggable } from './menu-tree-item-draggable';
import { MenuTreeItemDroppable } from './menu-tree-item-droppable';
import { MenuTreeItemEmpty } from './menu-tree-item-empty';
import { getIsMenuItemActiveParams, getMenuItemExpanded, getMenuItemParams } from './store-maps';

const permissionsRolePriority: OrgMemberRole[] = [
  OrgMemberRole.CLIENT,
  OrgMemberRole.GUEST,
  OrgMemberRole.MEMBER,
  OrgMemberRole.MANAGER,
  OrgMemberRole.OWNER,
];

export interface MenuTreeItemProps {
  menuType: MenuType;
  id: string;
  level: number;
  disabledDragItemTypes?: MenuItemType[];
  disabledDropItemTypes?: MenuItemType[];
  hideAddButtonItemTypes?: MenuItemType[];
  hideMenuButtonItemTypes?: MenuItemType[];
  showOpenInNewTabItemTypes?: MenuItemType[];
  allowClickIconItemTypes?: MenuItemType[];
  excludeIds?: string[];
  multiSelectEnabled?: boolean;
  isParentActive?: boolean;
  emptyFolderText?: string;
  tooltipMap?: MenuTooltipMap;
  activeDragId?: string;
  activeHighlightDisabled?: boolean;
  onMenuItemToggle: (item: MenuItem) => void;
  onMenuItemClick: (item: MenuItem) => void;
  onMenuItemHover: (item: MenuItem) => void;
  onMoreClick: (item: MenuItem | undefined, event: MouseEvent) => void;
  onAddClick: (item: MenuItem | undefined, event: MouseEvent) => void;
  onMenuItemOpenInNewTab?: (item: MenuItem) => void;
  onDragOver?: (id: string) => void;
  onIconClick: (item: MenuItem, event: MouseEvent) => void;
  customRenderers: Record<string, FunctionComponent<MenuItem>> | undefined;
  customClasses: Record<number, string> | undefined;
  onMenuItemRightClick?: (item: MenuItem | undefined, event: MouseEvent) => void;
  permissionPreviewEnabled?: boolean;
  onPermissionClick?: (item: MenuItem) => void;
  usersWithRole?: UserWithRoleAndAccess[];
  userId?: number;
  /**
   * Limits number of displayed children items
   * truncates long children lists and displays a "Show more" button
   */
  maxChildrenItemsToRender?: number;
  /**
   * Called when user clicks on "Show more..." button when children are truncated
   * @param menuItem - parent item which children are truncated
   */
  onMoreItemsClick?: (menuItem: MenuItem) => void;
  parentItem?: MenuItem;
  forceActiveId?: string;
}

const Item: FunctionComponent<MenuTreeItemProps> = ({
  menuType,
  id,
  level,
  disabledDragItemTypes = [],
  disabledDropItemTypes = [],
  hideAddButtonItemTypes = [],
  hideMenuButtonItemTypes = [],
  showOpenInNewTabItemTypes = [],
  allowClickIconItemTypes = [],
  excludeIds = [],
  multiSelectEnabled,
  isParentActive = false,
  emptyFolderText,
  tooltipMap,
  activeDragId,
  activeHighlightDisabled,
  onMenuItemToggle,
  onMenuItemClick,
  onMenuItemHover,
  onMoreClick,
  onAddClick,
  onMenuItemOpenInNewTab,
  onDragOver,
  onIconClick,
  customRenderers = {},
  customClasses = {},
  onMenuItemRightClick,
  permissionPreviewEnabled,
  onPermissionClick,
  usersWithRole,
  userId,
  maxChildrenItemsToRender,
  onMoreItemsClick,
  parentItem,
  forceActiveId,
}) => {
  const item: MenuItem | undefined = useStoreMap(getMenuItemParams(id, menuType));
  const isExpanded: boolean = useStoreMap(getMenuItemExpanded(id));
  const exclude = useMemo(() => {
    return excludeIds.includes(id);
  }, [id, excludeIds]);

  const nextLevel = useMemo(() => {
    return level + 1;
  }, [level]);

  const isActiveStoreValue = useStoreMap(getIsMenuItemActiveParams(id, menuType));
  const isActive = useMemo(() => {
    if (activeHighlightDisabled) {
      return false;
    }
    if (forceActiveId) {
      return item?.id === forceActiveId;
    }
    return isActiveStoreValue;
  }, [activeHighlightDisabled, forceActiveId, isActiveStoreValue, item?.id]);

  const isChildrenActive = useMemo(() => {
    return isParentActive || (isActive && multiSelectEnabled);
  }, [isParentActive, isActive, multiSelectEnabled]);

  const handleMoreClick = useCallback(
    (menuItem: MenuItem, event: MouseEvent) => {
      if (onMoreClick) {
        onMoreClick(menuItem, event);
      }
    },
    [onMoreClick]
  );

  const handleAddClick = useCallback(
    (menuItem: MenuItem, event: MouseEvent) => {
      if (onAddClick) {
        onAddClick(menuItem, event);
      }
    },
    [onAddClick]
  );

  const handleIconClick = useCallback(
    (menuItem: MenuItem, event: MouseEvent) => {
      if (onIconClick) {
        onIconClick(menuItem, event);
      }
    },
    [onIconClick]
  );

  const onMoreItemsClickCallback = useCallback(() => {
    onMoreItemsClick && onMoreItemsClick(item);
  }, [item, onMoreItemsClick]);

  if (!item || exclude || item.deleted) {
    return null;
  }

  const customRenderer = customRenderers ? customRenderers[item.type] : null;

  const hideAddButton = (hideAddButtonItemTypes && hideAddButtonItemTypes.includes(item.type)) || !!item.originGlobalId;

  let itemElement: ReactElement | FunctionComponent<MenuItem> | ReactNode | null;
  const allowClickIconItem = allowClickIconItemTypes && allowClickIconItemTypes.includes(item.type);

  if (customRenderer) {
    itemElement = customRenderer(item);
  } else {
    itemElement = defaultItemRenderer(
      item,
      isActive || isParentActive,
      level,
      isExpanded,
      customClasses,
      (item) => onMenuItemToggle(item),
      (item) => onMenuItemClick(item),
      (item) => onMenuItemHover(item),
      (item, event) => handleMoreClick(item, event),
      (item, event) => handleAddClick(item, event),
      (item, event) => handleIconClick(item, event),
      hideAddButton,
      hideMenuButtonItemTypes,
      allowClickIconItem,
      tooltipMap,
      onMenuItemRightClick ? (item, event) => onMenuItemRightClick(item, event) : undefined,
      permissionPreviewEnabled,
      usersWithRole,
      onPermissionClick,
      userId,
      parentItem,
      showOpenInNewTabItemTypes,
      (item) => onMenuItemOpenInNewTab?.(item)
    );
  }

  const order = item.order;
  const showChildren = order && order.length > 0 && isExpanded;

  let childrenItems = order;
  let childrenListTruncated = false;
  if (childrenItems && maxChildrenItemsToRender) {
    if (childrenItems.length > maxChildrenItemsToRender) {
      childrenItems = childrenItems.slice(0, maxChildrenItemsToRender);
      childrenListTruncated = true;
    }
  }

  const showMoreChildrenItem = childrenListTruncated && (
    <MenuTreeItemAction onClick={onMoreItemsClickCallback} level={level} text="More on the pages list..." />
  );

  return (
    <div>
      {itemElement}
      {isSectionMenuItemType(item) && !order?.length && isExpanded && emptyFolderText && (
        <MenuTreeItemDroppable
          id={id}
          key={`droppable-${id}`}
          level={nextLevel}
          isEmpty={true}
          activeDragId={activeDragId}
          menuType={menuType}
          onDragOver={() => (onDragOver ? onDragOver(id) : false)}
        >
          <MenuTreeItemEmpty
            level={level}
            text={emptyFolderText}
            hideAddButton={hideAddButton}
            onMenuItemAdd={(event) => handleAddClick(item, event)}
            permissionPreviewEnabled={permissionPreviewEnabled}
          />
        </MenuTreeItemDroppable>
      )}
      {showChildren && childrenItems && (
        <div>
          {childrenItems.map((id) => (
            <MenuTreeItemDraggable
              id={id}
              key={`draggable-${id}`}
              menuType={menuType}
              disabledDragItemTypes={disabledDragItemTypes}
            >
              <MenuTreeItemDroppable
                id={id}
                key={`droppable-${id}`}
                level={nextLevel}
                activeDragId={activeDragId}
                menuType={menuType}
                onDragOver={() => (onDragOver ? onDragOver(id) : false)}
                disabledDropItemTypes={disabledDropItemTypes}
              >
                <MenuTreeItem
                  menuType={menuType}
                  key={id}
                  id={id}
                  level={nextLevel}
                  excludeIds={excludeIds}
                  hideAddButtonItemTypes={hideAddButtonItemTypes}
                  hideMenuButtonItemTypes={hideMenuButtonItemTypes}
                  showOpenInNewTabItemTypes={showOpenInNewTabItemTypes}
                  allowClickIconItemTypes={allowClickIconItemTypes}
                  isParentActive={isChildrenActive}
                  multiSelectEnabled={multiSelectEnabled}
                  emptyFolderText={emptyFolderText}
                  tooltipMap={tooltipMap}
                  activeDragId={activeDragId}
                  activeHighlightDisabled={activeHighlightDisabled}
                  onMenuItemToggle={(item) => onMenuItemToggle(item)}
                  onMenuItemClick={(item) => onMenuItemClick(item)}
                  onMenuItemHover={(item) => onMenuItemHover(item)}
                  onMoreClick={(item, event) => onMoreClick(item, event)}
                  onAddClick={(item, event) => onAddClick(item, event)}
                  onMenuItemOpenInNewTab={(item) => onMenuItemOpenInNewTab?.(item)}
                  onIconClick={(item, event) => onIconClick(item, event)}
                  customRenderers={customRenderers}
                  customClasses={customClasses}
                  onDragOver={onDragOver}
                  onMenuItemRightClick={
                    onMenuItemRightClick ? (menuItem, event) => onMenuItemRightClick(menuItem, event) : undefined
                  }
                  permissionPreviewEnabled={permissionPreviewEnabled}
                  onPermissionClick={onPermissionClick}
                  usersWithRole={usersWithRole}
                  userId={userId}
                  maxChildrenItemsToRender={maxChildrenItemsToRender}
                  onMoreItemsClick={onMoreItemsClick}
                  parentItem={item}
                  forceActiveId={forceActiveId}
                />
              </MenuTreeItemDroppable>
            </MenuTreeItemDraggable>
          ))}
          {showMoreChildrenItem}
        </div>
      )}
    </div>
  );
};

export const MenuTreeItem = memo(Item);

function defaultItemRenderer(
  item: MenuItem,
  isActive: boolean,
  level: number,
  isExpanded: boolean,
  customClasses: Record<number, string> | undefined,
  onToggle: (item: MenuItem) => void,
  onSelect: (item: MenuItem, event: MouseEvent) => void,
  onHover: (item: MenuItem) => void,
  moreClick: (item: MenuItem, event: MouseEvent) => void,
  addClick: (item: MenuItem, event: MouseEvent) => void,
  iconClick: (item: MenuItem, event: MouseEvent) => void,
  hideAddButton?: boolean,
  hideMenuButtonItemTypes?: MenuItemType[],
  allowIconClick?: boolean,
  tooltipMap?: MenuTooltipMap,
  onContextMenu?: (item: MenuItem, event: MouseEvent) => void,
  permissionPreviewEnabled?: boolean,
  usersWithRole?: UserWithRoleAndAccess[],
  handlePermissionClick?: (item: MenuItem) => void,
  userId?: number,
  parentItem?: MenuItem,
  showOpenInNewTabButtonItemTypes?: MenuItemType[],
  openInNewTabClick?: (item: MenuItem) => void
): ReactElement | null {
  const paddingLeft = getItemOffset(level, permissionPreviewEnabled);

  const itemSelectedCustomClasses = customClasses ? customClasses[MenuItemCustomClass.ITEM_SELECTED] : '';
  const itemCustomClasses = customClasses ? customClasses[MenuItemCustomClass.ITEM] : '';
  const invisible = 'visible' in item && !item.visible;

  const defaultClasses = classNames('flex items-center py-2 pr-4 filter-none cursor-pointer rounded group', {
    'hover:bg-neutral-100': !isActive,
    'text-neutral-400': invisible,
  });
  const activeClasses = isActive ? itemSelectedCustomClasses ?? 'bg-teal-100 hover:bg-teal-100' : '';

  const hideMenuButton = hideMenuButtonItemTypes && hideMenuButtonItemTypes.includes(item.type);

  let addButtonTooltip: string | undefined = undefined;
  let moreButtonTooltip: string | undefined = undefined;
  if (tooltipMap) {
    const addButton = tooltipMap[MenuTooltipPosition.ADD_BUTTON];
    const moreButton = tooltipMap[MenuTooltipPosition.MORE_BUTTON];
    if (addButton && addButton[item.type]) {
      addButtonTooltip = addButton[item.type] as string;
    }
    if (moreButton && moreButton[item.type]) {
      moreButtonTooltip = moreButton[item.type] as string;
    }
  }
  const showToggleArrow =
    item.type === MenuItemType.PORTAL_SECTION ||
    item.type === MenuItemType.PORTAL_PROCESS ||
    item.type === MenuItemType.NOTES_FOLDER ||
    item.type === MenuItemType.FOLDERS ||
    item.type === MenuItemType.TAGS ||
    item.hasChildren;

  const hideIcon = item.type === MenuItemType.TAG;

  const showPermissionsBlock = usersWithRole && permissionPreviewEnabled && level === 0;

  const showOpenInNewTab = showOpenInNewTabButtonItemTypes && showOpenInNewTabButtonItemTypes.includes(item.type);

  return (
    <div
      data-id={item.id}
      id={item.id}
      className={`${defaultClasses} ${itemCustomClasses ?? ''} ${activeClasses}`}
      style={{ paddingLeft }}
      onClick={(event) => {
        event.preventDefault();
        onSelect(item, event);
      }}
      onMouseEnter={() => onHover(item)}
      onContextMenu={(event) => {
        if (onContextMenu) {
          event.preventDefault();
          onContextMenu(item, event);
        }
      }}
    >
      {showPermissionsBlock && item.permissions && handlePermissionClick && (
        <ItemPermissionPreview
          permissions={item.permissions}
          usersWithRole={usersWithRole}
          nodeName={item.name}
          userId={userId}
          type={item.type}
          onOpen={() => handlePermissionClick(item)}
        />
      )}

      <div className="w-4 h-4" style={{ minWidth: '16px' }}>
        {showToggleArrow && (
          <div
            data-cy="menu-item-toggle"
            className="cursor-pointer"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onToggle(item);
            }}
          >
            <Icon
              icon={IconsMap.ARROW_DOWN}
              className={`text-neutral-500 w-4 h-4 ${!isExpanded ? '-rotate-90' : ''}`}
            />
          </div>
        )}
      </div>
      <div className="flex gap-2 items-center min-w-0 mr-2">
        {!hideIcon && (
          <div
            className={`w-4 h-4 flex items-center text-neutral-500 justify-center min-w-[16px] ${
              allowIconClick ? 'hover:bg-neutral-200 rounded' : ''
            }`}
            onClick={(event) => {
              if (allowIconClick) {
                event.preventDefault();
                event.stopPropagation();
                iconClick(item, event);
              }
            }}
          >
            <ItemIcon item={item} parentItem={parentItem} />
          </div>
        )}
        <span
          title={item.name}
          className={`text-sm whitespace-nowrap text-ellipsis overflow-hidden ${
            isActive ? 'font-bold' : 'font-normal'
          }`}
        >
          {item.name}
        </span>
      </div>
      <div className="ml-auto group-hover:flex gap-2">
        <div className="hidden group-hover:flex gap-2">
          {!hideMenuButton && (
            <MenuIconButton
              type="more"
              icon={IconsMap.MORE_HORIZONTAL}
              tooltip={moreButtonTooltip}
              onClick={(event) => moreClick(item, event)}
            />
          )}
          {!hideAddButton && (
            <MenuIconButton
              type="plus"
              tooltip={addButtonTooltip}
              icon={IconsMap.PLUS}
              onClick={(event) => addClick(item, event)}
            />
          )}
        </div>
        {showOpenInNewTab && (
          <MenuIconButton
            type="go-to"
            tooltip={addButtonTooltip}
            icon={IconsMap.OPEN}
            onClick={() => openInNewTabClick?.(item)}
            iconClassName="!text-cyan-900"
          />
        )}
      </div>
      {invisible && (
        <div data-cy="icon-eye" className="ml-auto group-hover:hidden">
          <div className="w-4 h-4">
            <Icon icon={IconsMap.EYE_CLOSE} />
          </div>
        </div>
      )}
    </div>
  );
}

const permissionVisibleUserCount = 4;
const ItemPermissionPreview: FunctionComponent<{
  type: MenuItemType;
  nodeName: string;
  permissions: Permissions;
  usersWithRole: UserWithRoleAndAccess[];
  onOpen: () => void;
  userId?: number;
}> = ({ type, nodeName, permissions, usersWithRole, onOpen, userId }) => {
  const allowAll = permissions.allowAll;

  const itemUsers = usersWithRole.reduce((acc, user) => {
    if (user.hasPortalConsoleAccess) {
      acc.push(user);
    } else if (user.hasPortalAccess && permissions.allowUserIds.includes(user.id)) {
      acc.push(user);
    }
    return acc;
  }, [] as UserWithRoleAndAccess[]);

  const itemUser = findUserWithHighestPriority(itemUsers, permissionsRolePriority);
  const userName = getUserDisplayName(itemUser, userId ?? 0);
  const moreUsersCount = itemUsers.length - permissionVisibleUserCount;
  const nodeTypeName = getNodeNameByType(type);
  const [tooltipsHidden, setTooltipsHidden] = useState(false);

  const handleClick = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (type !== MenuItemType.HOME) {
      onOpen();
      setTooltipsHidden(true);
      setTimeout(() => {
        setTooltipsHidden(false);
      }, 500);
    }
  };

  return (
    <div className="w-[30px] min-w-[30px] ml-2" onClick={(event) => event.stopPropagation()}>
      {itemUser && !allowAll && (
        <TippyTooltip
          show={!tooltipsHidden}
          position="auto"
          interactive={true}
          message={
            <div className="p-1 max-w-[150px]">
              <div className="mb-2 first-letter:uppercase">
                {nodeTypeName} “{nodeName}” shared for:{' '}
              </div>
              {itemUsers.slice(0, permissionVisibleUserCount).map((user) => {
                const userName = getUserDisplayName(user, userId ?? 0);
                return (
                  <div key={user.id} className="flex items-center mb-1">
                    <Avatar size="xs" name={userName} avatarUrl={user.avatar} />{' '}
                    <span className="ml-1 text-[10px] whitespace-nowrap overflow-hidden text-ellipsis">{userName}</span>
                  </div>
                );
              })}
              {moreUsersCount > 0 && <div className="text-[10px] text-teal-400">+{moreUsersCount} more users</div>}
              <div className="flex justify-end mt-2">
                <Button
                  type={ButtonType.STROKED}
                  className="border-teal-400 text-teal-400 hover:border-teal-500 hover:text-teal-400"
                  size={ButtonSize.SM}
                  onClick={handleClick}
                >
                  Open details
                </Button>
              </div>
            </div>
          }
        >
          <div className="flex items-center justify-end" onClick={handleClick}>
            <Avatar name={userName} avatarUrl={itemUser.avatar} bgClass="bg-teal-400" size="xs" />
            {itemUsers.length > 1 && <div className="text-[8px] font-semibold pl-[2px]">+{itemUsers.length - 1}</div>}
          </div>
        </TippyTooltip>
      )}
      {allowAll && (
        <div
          className={classNames('w-4 h-4 bg-neutral-100 rounded-full flex items-center justify-center ml-auto', {
            'cursor-default': type === MenuItemType.HOME,
          })}
          onClick={handleClick}
        >
          <TippyTooltip
            position="auto"
            interactive={false}
            show={true}
            message={
              <div className="max-w-[170px]">
                {type === MenuItemType.HOME ? (
                  <span>
                    Home Page shared for all <br />
                    members of the portal. <br /> You cannot change <br /> its access settings
                  </span>
                ) : (
                  <span>
                    “{nodeName}” <br /> shared for all members of <br /> the portal. Click on the <br /> icon to change
                    access <br /> settings
                  </span>
                )}
              </div>
            }
          >
            <div>
              <Icon className="w-3 h-3 text-neutral-400" icon={IconsMap.PUBLIC} />
            </div>
          </TippyTooltip>
        </div>
      )}
    </div>
  );
};

const ItemIcon: FunctionComponent<{ item: MenuItem; parentItem?: MenuItem }> = ({ item, parentItem }) => {
  if (item.iconUrl) {
    return <img src={item.iconUrl} alt="" className="max-w-4 max-h-4" />;
  } else if (item.icon && Object.keys(IconsMap).includes(item.icon)) {
    return <Icon icon={item.icon as IconKey} className="w-4 h-4" />;
  } else {
    const icon = getIconByMenuItem({ menuItem: item, parentMenuItem: parentItem, allowProjectionIcons: true });
    return <Icon icon={getMenuItemIcon({ type: item.type, icon })} className="w-4 h-4" />;
  }
};

const MenuIconButton: FunctionComponent<{
  icon: ReactElement;
  tooltip: string | undefined;
  onClick: (event: MouseEvent) => void;
  type?: string;
  iconClassName?: string;
}> = ({ tooltip, icon, onClick, type, iconClassName }) => {
  const button = (
    <div
      data-cy={type}
      className="w-4 h-4 rounded hover:bg-neutral-200"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onClick(event);
      }}
    >
      <Icon icon={icon} className={classNames('text-neutral-500', iconClassName)} />
    </div>
  );

  if (tooltip) {
    return (
      <Tooltip message={tooltip} position={UiTooltipPosition.TOP_LEFT}>
        {button}
      </Tooltip>
    );
  } else {
    return button;
  }
};

function findUserWithHighestPriority(
  users: UserWithRoleAndAccess[],
  rolePriority: OrgMemberRole[]
): UserWithRoleAndAccess | undefined {
  for (const role of rolePriority) {
    const foundUser = users.find((user) => user.role === role);
    if (foundUser) {
      return foundUser;
    }
  }
  return undefined;
}

export function getNodeNameByType(type: MenuItemType) {
  if (type === MenuItemType.FOLDERS || type === MenuItemType.NOTES_FOLDER || type === MenuItemType.PORTAL_SECTION) {
    return 'folder';
  } else if (type === MenuItemType.PORTAL_PROCESS) {
    return 'folder';
  } else if (type === MenuItemType.LINK) {
    return 'link';
  } else {
    return 'page';
  }
}
